import { instantToEpochMs } from './calendar.js';
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
/**
 * Delivery semantics per command type.
 *
 * SPEC.md asked for commands that are "idempotent where possible" and
 * "rejectable when expired" without defining either. Both live here, because
 * the backend and the device must agree: the backend decides what to keep while
 * a device is offline, and the device decides what to honour once it wakes up.
 *
 * The distinctions are not cosmetic. A `PAUSE` delivered three hours late stops
 * a session the parent never meant to stop, so it is dropped; `ADD_TIME` is
 * still correct whenever it lands, so it is queued; repeated `REFRESH_CONFIG`
 * commands collapse to the newest one.
 */
export const COMMAND_DELIVERY = {
    PAUSE: { ttlMs: 60 * SECOND, offline: 'DROP' },
    RESUME: { ttlMs: 60 * SECOND, offline: 'DROP' },
    END_SESSION: { ttlMs: 5 * MINUTE, offline: 'DROP' },
    ADD_TIME: { ttlMs: 15 * MINUTE, offline: 'QUEUE' },
    LOCK: { ttlMs: 15 * MINUTE, offline: 'QUEUE' },
    UNLOCK: { ttlMs: 15 * MINUTE, offline: 'QUEUE' },
    REFRESH_CONFIG: { ttlMs: 24 * HOUR, offline: 'COALESCE' },
    REFRESH_CONTENT: { ttlMs: 24 * HOUR, offline: 'COALESCE' },
    // Opening a video an hour after a parent chose it would surprise everyone,
    // and a tablet that is not there to open it now has nobody in front of it.
    PLAY_CONTENT: { ttlMs: 60 * SECOND, offline: 'DROP' },
    // `satisfies` rather than a type annotation: it still fails to compile when a
    // command type is missing, while keeping the keys literal. Annotating with
    // `Record<RemoteCommandType, …>` would widen this into an index signature,
    // making every lookup optional even though every key is present.
};
/**
 * Whether a command has outlived its usefulness and must be refused.
 *
 * A command that arrives before it was issued is also refused: that means the
 * device clock is wrong, and acting on a command in that state is guesswork.
 */
export const isCommandExpired = (input) => {
    const ageMs = instantToEpochMs(input.at) - instantToEpochMs(input.issuedAt);
    if (ageMs < 0) {
        return true;
    }
    return ageMs > COMMAND_DELIVERY[input.type].ttlMs;
};
//# sourceMappingURL=command-delivery.js.map